/**
 * AccountLens ABI — specifically the getAccountEnabledVaultsInfo function.
 *
 * Returns all enabled vault data for an account in a single call:
 * shares, asset amounts, borrowed, collateral/controller status, and liquidity info.
 */
export const accountLensAbi = [
  {
    type: "function",
    name: "getAccountEnabledVaultsInfo",
    inputs: [
      { name: "evc", type: "address" },
      { name: "account", type: "address" },
    ],
    outputs: [
      {
        name: "",
        type: "tuple",
        components: [
          // EVCAccountInfo
          {
            name: "evcAccountInfo",
            type: "tuple",
            components: [
              { name: "timestamp", type: "uint256" },
              { name: "evc", type: "address" },
              { name: "account", type: "address" },
              { name: "addressPrefix", type: "bytes19" },
              { name: "owner", type: "address" },
              { name: "isLockdownMode", type: "bool" },
              { name: "isPermitDisabledMode", type: "bool" },
              { name: "lastAccountStatusCheckTimestamp", type: "uint256" },
              { name: "enabledControllers", type: "address[]" },
              { name: "enabledCollaterals", type: "address[]" },
            ],
          },
          // VaultAccountInfo[]
          {
            name: "vaultAccountInfo",
            type: "tuple[]",
            components: [
              { name: "timestamp", type: "uint256" },
              { name: "account", type: "address" },
              { name: "vault", type: "address" },
              { name: "asset", type: "address" },
              { name: "assetsAccount", type: "uint256" },
              { name: "shares", type: "uint256" },
              { name: "assets", type: "uint256" },
              { name: "borrowed", type: "uint256" },
              { name: "assetAllowanceVault", type: "uint256" },
              { name: "assetAllowanceVaultPermit2", type: "uint256" },
              { name: "assetAllowanceExpirationVaultPermit2", type: "uint256" },
              { name: "assetAllowancePermit2", type: "uint256" },
              { name: "balanceForwarderEnabled", type: "bool" },
              { name: "isController", type: "bool" },
              { name: "isCollateral", type: "bool" },
              // AccountLiquidityInfo
              {
                name: "liquidityInfo",
                type: "tuple",
                components: [
                  { name: "queryFailure", type: "bool" },
                  { name: "queryFailureReason", type: "bytes" },
                  { name: "account", type: "address" },
                  { name: "vault", type: "address" },
                  { name: "unitOfAccount", type: "address" },
                  { name: "timeToLiquidation", type: "int256" },
                  { name: "liabilityValueBorrowing", type: "uint256" },
                  { name: "liabilityValueLiquidation", type: "uint256" },
                  { name: "collateralValueBorrowing", type: "uint256" },
                  { name: "collateralValueLiquidation", type: "uint256" },
                  { name: "collateralValueRaw", type: "uint256" },
                  { name: "collaterals", type: "address[]" },
                  { name: "collateralValuesBorrowing", type: "uint256[]" },
                  { name: "collateralValuesLiquidation", type: "uint256[]" },
                  { name: "collateralValuesRaw", type: "uint256[]" },
                ],
              },
            ],
          },
          // AccountRewardInfo[]
          {
            name: "accountRewardInfo",
            type: "tuple[]",
            components: [
              { name: "timestamp", type: "uint256" },
              { name: "account", type: "address" },
              { name: "vault", type: "address" },
              { name: "balanceTracker", type: "address" },
              { name: "balanceForwarderEnabled", type: "bool" },
              { name: "balance", type: "uint256" },
              // EnabledRewardInfo[]
              {
                name: "enabledRewardsInfo",
                type: "tuple[]",
                components: [
                  { name: "reward", type: "address" },
                  { name: "earnedReward", type: "uint256" },
                  { name: "earnedRewardRecentIgnored", type: "uint256" },
                ],
              },
            ],
          },
        ],
      },
    ],
    stateMutability: "view",
  },
] as const;
